import { Menu } from 'antd';
import styled from 'styled-components';
import { theme } from '../../../theme/semanticTokens';
import type { MenuItemState, MenuItemType } from './types';

export const StyledMenuItem = styled(Menu.Item)<{
  $type: MenuItemType;
  $state: MenuItemState;
}>`
  padding: ${theme.dime.spacing.m} !important;
  border-radius: ${theme.dime.radius.s};
  margin: 0 !important;
  height: 32px !important;
  min-height: 32px !important;
  min-width: 180px !important;
  width: 100% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start;
  gap: ${theme.dime.spacing.m};
  /* Override Ant Design default styles that might affect vertical alignment */
  &::before {
    display: none !important;
  }

  /* Typography styling based on type */
  .menu-item-content {
    margin: 0 !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: flex-start;
    vertical-align: middle !important;
    flex: 0 1 auto !important;
    min-width: 0 !important;
    ${({ $type, $state }) => {
      if ($state === 'disabled') {
        return `
          color: ${theme.dime.colors.text.disabled};
        `;
      }

      switch ($type) {
        case 'destructive':
          return `
            color: ${theme.dime.colors.text.critical};
          `;
        case 'bold':
        case 'default':
        default:
          return `
            color: ${theme.dime.colors.text.primary};
          `;
      }
    }}
  }

  /* Default state - transparent background */
  background-color: transparent;

  /* Hover state - light grey background (when state prop is 'hover' or natural Ant Design hover) */
  ${({ $state }) =>
    $state === 'hover' &&
    `
    background-color: ${theme.dime.colors.surface.primaryHover} !important;
  `}

  /* Selected state - light blue background (when state prop is 'selected' or natural Ant Design selected) */
  ${({ $state }) =>
    $state === 'selected' &&
    `
    background-color: ${theme.dime.colors.surface.primarySelected} !important;
  `}

  /* Clicked state - pressed background */
  ${({ $state }) =>
    $state === 'clicked' &&
    `
    background-color: ${theme.dime.colors.surface.primaryPressed} !important;
  `}

  /* Disabled state - transparent background, disabled text color */
  ${({ $state }) =>
    $state === 'disabled' &&
    `
    background-color: transparent !important;
    cursor: not-allowed !important;
    opacity: 1 !important;
  `}

  /* Natural Ant Design hover state */
  &:hover:not(.ant-menu-item-disabled):not(.ant-menu-item-selected) {
    ${({ $state }) =>
      $state === 'default' &&
      `
      background-color: ${theme.dime.colors.surface.primaryHover};
    `}
  }

  /* Natural Ant Design selected state */
  &.ant-menu-item-selected:not(.ant-menu-item-disabled) {
    ${({ $state }) =>
      $state === 'default' &&
      `
      background-color: ${theme.dime.colors.surface.primarySelected} !important;
    `}
  }

  /* Ant Design disabled state */
  &.ant-menu-item-disabled {
    background-color: transparent !important;
    cursor: not-allowed !important;
    opacity: 1 !important;

    .menu-item-content {
      color: ${theme.dime.colors.text.disabled} !important;
    }
  }
`;

export const MenuItemContent = styled.div<{ $showArrow?: boolean }>`
  display: flex !important;
  align-items: center !important;
  justify-content: ${({ $showArrow }) =>
    $showArrow ? 'space-between' : 'flex-start'} !important;
  gap: ${theme.dime.spacing.m};
  flex: 1 1 100% !important;
  width: 100% !important;
  min-width: 0 !important;
`;

export const MenuItemArrow = styled.div<{
  $type: MenuItemType;
  $state: MenuItemState;
}>`
  display: flex !important;
  align-items: center !important;
  margin-left: auto !important;
  flex-shrink: 0 !important;
  flex-grow: 0 !important;
  order: 999 !important;
  color: ${({ $type, $state }) => {
    if ($state === 'disabled') {
      return theme.dime.colors.text.disabled;
    }
    switch ($type) {
      case 'destructive':
        return theme.dime.colors.text.critical;
      default:
        return theme.dime.colors.text.primary;
    }
  }};
`;
